import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, KineticLines } from "@/components/Reveal";

const clients = [
  { name: "UNICEF", sector: "Development & Child Rights" },
  { name: "WHO-SEARO", sector: "Global Health" },
  { name: "UN World Food Programme", sector: "Humanitarian & Food Security" },
  { name: "WWF-India", sector: "Sustainability & Conservation" },
  { name: "Bharti Foundation", sector: "Education & Social Impact" },
];

const sectors = ["Corporate Communications", "Development", "Healthcare", "Education", "Sustainability", "Social Impact"];

const ambitions = ["Build Reputation", "Shape Conversations", "Position Leaders", "Enter New Markets", "Strengthen Influence", "Tell Better Stories"];

const TEAM_IMG =
  "https://images.unsplash.com/photo-1622675363311-3e1904dc1885?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxjcmVhdGl2ZSUyMGFnZW5jeSUyMGRpdmVyc2UlMjB0ZWFtJTIwc3RyYXRlZ3l8ZW58MHx8fHwxNzg2MzYzMzk3fDA&ixlib=rb-4.1.0&q=85";

const ARCH_IMG =
  "https://images.unsplash.com/photo-1565626424178-c699f6601afd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGJydXRhbGlzdCUyMGFyY2hpdGVjdHVyZSUyMHNoYWRvd3xlbnwwfHx8fDE3ODYzNjMzOTd8MA&ixlib=rb-4.1.0&q=85";

export default function Work() {
  return (
    <main data-testid="work-page">
      <section className="mx-auto max-w-[1600px] px-6 pb-16 pt-40 sm:px-12 sm:pt-52" data-testid="work-hero">
        <p className="mb-8 text-xs uppercase tracking-[0.3em] text-primary">Our Experience</p>
        <KineticLines
          lines={["Work that carries", "the weight of institutions."]}
          className="font-syne text-[1.75rem] font-extrabold uppercase leading-[1.45] tracking-wider sm:text-[2.4rem] lg:text-[3.9rem]"
        />
        <Reveal delay={0.3}>
          <p className="mt-10 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
            Creative Curve has worked across development, healthcare, education, sustainability, social impact and
            corporate communications — partnering with leading institutions, organisations and businesses.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-[1600px] px-6 py-16 sm:px-12" data-testid="work-clients-section">
        <div className="border-t border-black/10">
          {clients.map((c, i) => (
            <Reveal key={c.name} delay={i * 0.05}>
              <div
                className="group grid grid-cols-1 items-baseline gap-2 border-b border-black/10 py-8 sm:grid-cols-12 sm:gap-8 sm:py-10"
                data-testid={`work-client-${i}`}
              >
                <span className="font-syne text-sm text-muted-foreground sm:col-span-1">0{i + 1}</span>
                <h2 className="font-syne text-3xl font-extrabold uppercase tracking-wide transition-colors duration-300 group-hover:text-primary sm:col-span-7 sm:text-5xl">
                  {c.name}
                </h2>
                <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground sm:col-span-4 sm:text-right">
                  {c.sector}
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1600px] px-6 py-16 sm:px-12" data-testid="work-wfp-highlight">
        <Reveal>
          <div className="grid grid-cols-1 gap-px bg-black/10 lg:grid-cols-5">
            <div className="bg-background p-10 sm:p-14 lg:col-span-3">
              <p className="text-xs uppercase tracking-[0.3em] text-primary">Standing Engagement</p>
              <h2 className="mt-6 font-syne text-2xl font-extrabold uppercase leading-tight tracking-wide sm:text-4xl">
                Long-Term Agreement with the UN World Food Programme
              </h2>
              <p className="mt-6 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base">
                Reflecting our experience working with the communication standards and requirements of major
                international institutions.
              </p>
            </div>
            <div className="relative min-h-[280px] overflow-hidden bg-background lg:col-span-2">
              <img src={ARCH_IMG} alt="Abstract architectural forms" className="absolute inset-0 h-full w-full object-cover opacity-60 transition-transform duration-700 hover:scale-105" />
            </div>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-[1600px] px-6 py-16 sm:px-12" data-testid="work-sectors-section">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-2">
          <Reveal>
            <div className="relative overflow-hidden">
              <img src={TEAM_IMG} alt="Strategy session at Creative Curve" className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-105" />
              <span className="absolute bottom-4 left-4 bg-background/70 px-4 py-2 text-[10px] uppercase tracking-[0.25em] backdrop-blur-md">
                Strategy in session
              </span>
            </div>
          </Reveal>
          <div>
            <Reveal>
              <h2 className="font-syne text-3xl font-extrabold uppercase tracking-wide sm:text-4xl">
                Sectors of <span className="text-primary">practice</span>
              </h2>
            </Reveal>
            <div className="mt-10 flex flex-wrap gap-3">
              {sectors.map((s, i) => (
                <Reveal key={s} delay={i * 0.05}>
                  <span className="border border-black/15 px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-foreground/80 transition-colors duration-300 hover:border-primary hover:text-primary" data-testid={`work-sector-${i}`}>
                    {s}
                  </span>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <p className="font-editorial mt-14 text-2xl leading-snug text-foreground/80 sm:text-3xl">
                "A partner for ambition — for organisations ready to be understood, remembered and acted upon."
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-black/10" data-testid="work-ambition-section">
        <div className="mx-auto max-w-[1600px] px-6 py-24 sm:px-12 sm:py-32">
          <Reveal>
            <p className="mb-4 text-xs uppercase tracking-[0.3em] text-muted-foreground">A Partner for Ambition</p>
            <h2 className="font-syne text-3xl font-extrabold uppercase tracking-wide sm:text-5xl">
              We work with organisations looking to
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-px bg-black/10 sm:grid-cols-2 lg:grid-cols-3">
            {ambitions.map((a, i) => (
              <Reveal key={a} delay={i * 0.06} className="bg-background">
                <div className="group flex items-center justify-between p-8 transition-colors duration-500 hover:bg-muted" data-testid={`work-ambition-${i}`}>
                  <span className="font-syne text-lg font-bold uppercase tracking-wide transition-colors duration-300 group-hover:text-primary">{a}</span>
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-primary" />
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/contact"
              data-testid="work-cta-button"
              className="group mt-16 flex w-max items-center gap-3 border border-foreground/30 px-8 py-4 font-syne text-sm font-bold uppercase tracking-[0.15em] transition-colors duration-300 hover:border-foreground hover:bg-foreground hover:text-background"
            >
              Make it happen
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
